import {
  FaBullseye,
  FaCalendarAlt,
  FaClock,
  FaEuroSign,
  FaMapMarkerAlt,
  FaTag,
  FaUsers,
} from 'react-icons/fa';
import { resolvePublicProposal } from '../../utils/resolvePublicProposal';

export const CIL_COLORS = {
  yellow: '#ffcc00',
  green: '#3eb297',
  blue: '#36a9e1',
  pink: '#e95995',
  purple: '#3b235a',
  red: '#e73341',
  bg: '#fff9ec',
  white: '#ffffff',
  text: '#2d2440',
};

export const categoriaLabels = {
  educazione: 'Educazione',
  laboratorio: 'Laboratorio',
  evento: 'Evento',
  esperienza: 'Esperienza',
  rassegna: 'Rassegna',
  torneo: 'Torneo',
  corso: 'Corso',
  gita: 'Gita',
  gita_didattica: 'Gita didattica',
};

export const locationLabels = {
  sede_cil: 'Sede CIL',
  scuola: 'Presso scuola',
  munlab: 'Munlab',
  outdoor: 'Outdoor',
  indoor: 'Indoor',
  territorio: 'Territorio',
  altro: 'Altro',
};

export const targetLabels = {
  infanzia: 'Scuola infanzia',
  primaria: 'Scuola primaria',
  secondaria_1: 'Secondaria I grado',
  secondaria_2: 'Secondaria II grado',
  '0_6': '0-6 anni',
  '6_11': '6-11 anni',
  '6_8': '6-8 anni',
  '9_11': '9-11 anni',
  '12_14': '12-14 anni',
  '15_18': '15-18 anni',
  adulti: 'Adulti',
  famiglie: 'Famiglie',
};

export const durationLabels = {
  '1h': '1 ora',
  '2h': '2 ore',
  '3h': '3 ore',
  mezza_giornata: 'Mezza giornata',
  giornata_intera: 'Giornata intera',
  piu_incontri: 'Piu incontri',
  annuale: 'Annuale',
};

export const experienceTypeLabels = {
  outdoor: 'Outdoor / territorio',
  creativo: 'Creativo',
  gioco: 'Gioco',
  culturale: 'Culturale',
  educativo: 'Educativo',
  musicale: 'Musicale',
  social: 'Social',
};

export const formatLabels = {
  evento: 'Evento',
  workshop: 'Workshop',
  aperitivo: 'Aperitivo',
  corso: 'Corso',
  concerto: 'Concerto',
  dj_set: 'DJ set',
};

export const activityTypeLabels = {
  gaming: 'Gaming / gioco organizzato',
  creativo: 'Creativo / laboratorio',
  spettacolo: 'Spettacolo / esperienza',
  evento: 'Evento / festa',
};

export const autonomyLevelLabels = {
  autonomo: 'Autonomo',
  semi_autonomo: 'Semi-autonomo',
};

export const socialValueLabels = {
  community: 'Community',
  competizione: 'Competizione',
  espressione: 'Espressione',
};

export const getDurationTextList = (durationValue) => {
  const durationList = Array.isArray(durationValue)
    ? durationValue
    : durationValue
      ? [durationValue]
      : [];
  return durationList.filter(Boolean).map((value) => {
    if (durationLabels[value]) return durationLabels[value];
    const normalized = String(value || '').trim();
    return normalized
      .replaceAll('_', ' ')
      .replace(/\s+/g, ' ')
      .replace(/^./, (char) => char.toUpperCase());
  });
};

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const normalizeEnumLabel = (value) => {
  if (!value) return '';
  const normalized = String(value).trim();
  return normalized
    .replaceAll('_', ' ')
    .replace(/\s+/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
};

const getPricingModeLabel = (mode) => {
  if (mode === 'per_persona') return 'Costo a persona';
  if (mode === 'per_pacchetto') return 'Costo a pacchetto';
  if (mode === 'per_gita') return 'Costo a gita';
  if (mode === 'per_totale') return 'Costo totale';
  return 'Costo a incontro';
};

const formatEuro = (amount) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) return null;
  return `${numericAmount} EUR`;
};

const getPricingDisplayValue = (rules, fallback) => {
  if (!Array.isArray(rules) || !rules.length) return fallback || null;

  const firstRule = rules[0] || {};
  const mode = firstRule.mode || 'per_incontro';

  if (mode === 'per_persona') {
    const amountValue = formatEuro(firstRule.unitAmount || firstRule.amount);
    if (!amountValue) return getPricingModeLabel(mode);
    return `${amountValue} / persona`;
  }

  if (mode === 'per_pacchetto') {
    const amountValue = formatEuro(firstRule.amount || firstRule.unitAmount);
    if (!amountValue) return getPricingModeLabel(mode);
    const meetingsValue =
      Number.isFinite(Number(firstRule.packageMeetings)) &&
      Number(firstRule.packageMeetings) > 0
        ? ` (${Number(firstRule.packageMeetings)} incontri)`
        : '';
    return `${amountValue} / pacchetto${meetingsValue}`;
  }

  if (mode === 'per_gita') {
    const amountValue = formatEuro(firstRule.amount || firstRule.unitAmount);
    if (!amountValue) return getPricingModeLabel(mode);
    return `${amountValue} / gita`;
  }

  if (mode === 'per_totale') {
    const amountValue = formatEuro(firstRule.amount || firstRule.unitAmount);
    if (!amountValue) return getPricingModeLabel(mode);
    return amountValue;
  }

  const amountValue = formatEuro(firstRule.amount || firstRule.unitAmount);
  if (!amountValue) return getPricingModeLabel(mode);
  return `${amountValue} / incontro`;
};

export const getTargetText = (resolved) => {
  const values = Array.isArray(resolved?.target) ? resolved.target : [];
  if (!values.length) return null;
  return values.map((value) => targetLabels[value] || value).join(', ');
};

export const getProposalHeroImageUrl = (proposal) => {
  const mediaUrl = proposal?.thumbnail?.url;
  if (!mediaUrl) return null;
  if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) {
    return mediaUrl;
  }
  const base = process.env.REACT_APP_STRAPI_API_URL || '';
  return `${base}${mediaUrl}`;
};

export const getMediaUrl = (media) => {
  const mediaUrl = media?.url;
  if (!mediaUrl) return null;
  if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) {
    return mediaUrl;
  }
  const base = process.env.REACT_APP_STRAPI_API_URL || '';
  return `${base}${mediaUrl}`;
};

const SCHOOL_EXCLUDED_DETAIL_KEYS = new Set([
  'parentalConsent',
  'notesForParents',
]);

const SCHOOL_EXCLUDED_TARGETS = new Set(['famiglie']);

export const buildProposalUiModel = (proposal, options = {}) => {
  const isSchoolAudience = options.audience === 'school';
  const resolved = resolvePublicProposal(proposal);
  const category = resolved.category
    ? categoriaLabels[resolved.category] || resolved.category
    : null;
  const rawTargets = Array.isArray(resolved.target) ? resolved.target : [];
  const scopedTargets = isSchoolAudience
    ? rawTargets.filter((value) => !SCHOOL_EXCLUDED_TARGETS.has(value))
    : rawTargets;
  const targetList = scopedTargets.map(
    (value) => targetLabels[value] || normalizeEnumLabel(value)
  );
  const target = targetList.length ? targetList.join(', ') : null;
  const periodList = toList(resolved.period).map((value) =>
    normalizeEnumLabel(value)
  );
  const normalizedDurationList = getDurationTextList(resolved.duration);
  const normalizedLocation =
    String(resolved.locationLabel || '').trim() ||
    locationLabels[resolved.location] ||
    normalizeEnumLabel(resolved.location);
  const objectives = toList(resolved.objectives);
  const keywords = toList(resolved.keywords);
  const skills = toList(resolved.skills);
  const benefits = toList(resolved.benefits);
  const pricingSummary =
    resolved?.pricing?.summary || resolved.priceRange || null;
  const pricingValue = getPricingDisplayValue(
    resolved?.pricing?.rules,
    pricingSummary
  );

  const detailItems = [
    {
      key: 'duration',
      label: 'Durata',
      value: normalizedDurationList.length ? normalizedDurationList : null,
      icon: FaClock,
    },
    {
      key: 'location',
      label: 'Luogo',
      value: normalizedLocation || null,
      icon: FaMapMarkerAlt,
    },
    {
      key: 'participants',
      label: 'Partecipanti',
      value:
        resolved.participants?.min || resolved.participants?.max
          ? `${resolved.participants?.min || '-'} - ${resolved.participants?.max || '-'}`
          : null,
      icon: FaUsers,
    },
    { key: 'target', label: 'Target', value: targetList, icon: FaBullseye },
    {
      key: 'period',
      label: 'Periodo',
      value: periodList.length ? periodList : null,
      icon: FaCalendarAlt,
    },
    {
      key: 'price',
      label: 'Tariffa',
      value: pricingValue,
      icon: FaEuroSign,
    },
  ];

  if (resolved.experienceType) {
    detailItems.push({
      key: 'experienceType',
      label: 'Tipo esperienza',
      value:
        experienceTypeLabels[resolved.experienceType] ||
        normalizeEnumLabel(resolved.experienceType),
      icon: FaTag,
    });
  }
  if (resolved.ageRange) {
    detailItems.push({
      key: 'ageRange',
      label: 'Età',
      value: resolved.ageRange,
      icon: FaUsers,
    });
  }
  if (resolved.format) {
    detailItems.push({
      key: 'format',
      label: 'Formato',
      value:
        formatLabels[resolved.format] || normalizeEnumLabel(resolved.format),
      icon: FaTag,
    });
  }
  if (resolved.level) {
    detailItems.push({
      key: 'level',
      label: 'Livello',
      value: normalizeEnumLabel(resolved.level),
      icon: FaTag,
    });
  }
  if (resolved.difficulty) {
    detailItems.push({
      key: 'difficulty',
      label: 'Difficoltà',
      value: normalizeEnumLabel(resolved.difficulty),
      icon: FaTag,
    });
  }
  if (resolved.activityType) {
    detailItems.push({
      key: 'activityType',
      label: 'Tipologia',
      value:
        activityTypeLabels[resolved.activityType] ||
        normalizeEnumLabel(resolved.activityType),
      icon: FaTag,
    });
  }
  if (resolved.autonomyLevel) {
    detailItems.push({
      key: 'autonomyLevel',
      label: 'Autonomia',
      value:
        autonomyLevelLabels[resolved.autonomyLevel] ||
        normalizeEnumLabel(resolved.autonomyLevel),
      icon: FaTag,
    });
  }
  if (resolved.socialValue) {
    detailItems.push({
      key: 'socialValue',
      label: 'Valore sociale',
      value:
        socialValueLabels[resolved.socialValue] ||
        normalizeEnumLabel(resolved.socialValue),
      icon: FaTag,
    });
  }
  if (resolved.parentalConsentRequired && !isSchoolAudience) {
    detailItems.push({
      key: 'parentalConsent',
      label: 'Consenso genitori',
      value: 'Richiesto',
      icon: FaTag,
    });
  }
  if (resolved.notesForParents?.trim() && !isSchoolAudience) {
    detailItems.push({
      key: 'notesForParents',
      label: 'Note per i genitori',
      value: resolved.notesForParents.trim(),
      icon: FaTag,
    });
  }

  const detailItemsFiltered = detailItems.filter(
    (item) =>
      item.value &&
      (!isSchoolAudience || !SCHOOL_EXCLUDED_DETAIL_KEYS.has(item.key))
  );

  return {
    title: resolved.title || proposal?.titolo || '',
    shortDescription: resolved.shortDescription || '',
    fullDescription: resolved.fullDescription || '',
    category,
    target,
    targetList,
    periodList,
    durationList: normalizedDurationList,
    objectives,
    keywords,
    skills,
    benefits,
    materials: resolved.materials || '',
    whatToBring: resolved.whatToBring || '',
    safetyNotes: resolved.safetyNotes || '',
    weatherFallback: resolved.weatherFallback || '',
    accessibility: resolved.accessibility || '',
    notes: resolved.notes || '',
    autonomyLevel: resolved.autonomyLevel || null,
    activityType: resolved.activityType || null,
    socialValue: resolved.socialValue || null,
    parentalConsentRequired: resolved.parentalConsentRequired,
    notesForParents: resolved.notesForParents || '',
    parentsRequired: resolved.parentsRequired,
    strollerFriendly: resolved.strollerFriendly,
    bookingRequired: resolved.bookingRequired,
    privateGroupAvailable: resolved.privateGroupAvailable,
    pricingSummary,
    pricingRules: Array.isArray(resolved?.pricing?.rules)
      ? resolved.pricing.rules
      : [],
    detailItems: detailItemsFiltered,
    heroImageUrl: getProposalHeroImageUrl(proposal),
    gallery: Array.isArray(proposal?.gallery) ? proposal.gallery : [],
    documents: Array.isArray(proposal?.documents) ? proposal.documents : [],
    ctaLabel: proposal?.ctaLabel || 'Richiedi informazioni',
    ctaUrl: resolved.cta?.url || '',
    publicSubtype: proposal?.publicSubtype || '',
  };
};
